
import { Notification } from '../types/notification.types';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { ClipboardList, MessageSquare } from 'lucide-react';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
}

const notificationIcons = {
  task_assigned: <ClipboardList className="w-5 h-5 text-blue-500" />,
  comment_added: <MessageSquare className="w-5 h-5 text-green-500" />,
};

// A simple time ago function, as we can't add new libraries now.
const timeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) {
    return Math.floor(interval) + " years ago";
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + " months ago";
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + " days ago";
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + " hours ago";
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + " minutes ago";
  }
  return Math.floor(seconds) + " seconds ago";
};

export const NotificationItem = ({ notification, onMarkAsRead }: NotificationItemProps) => {
  const link = `/${notification.relatedEntityType}s/${notification.relatedEntityId}`;

  return (
    <div
      className={cn(
        'p-4 flex items-start gap-4 transition-colors hover:bg-muted/50',
        {
          'bg-muted/20': notification.isRead,
        }
      )}
    >
      <div className="flex-shrink-0 mt-1">
        {notificationIcons[notification.type] || <ClipboardList className="w-5 h-5 text-gray-500" />}
      </div>
      <div className="flex-grow">
        <Link to={link} className="text-sm font-medium leading-snug hover:underline">
          {notification.message}
        </Link>
        <p className="text-xs text-muted-foreground mt-1">
          {timeAgo(notification.createdAt)}
        </p>
      </div>
      {!notification.isRead && (
        <div className="flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => onMarkAsRead(notification.id)}>
            Mark as read
          </Button>
        </div>
      )}
    </div>
  );
};
